/* eslint-disable camelcase */
import { Emitter } from '@leyan/emitter';
import { PartialLocation } from 'history';
import { decode } from 'jsonwebtoken';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useLocation } from 'react-router-dom';
import useQuery, { useCreateSearch } from '../../hooks/useQuery';
import useTrackingRef from '../../hooks/useTrackingRef';
import { storeIdCache, superBookTokenCache } from '../../services/cache';
import createNamedContext from '../../utils/createNamedContext';
import refine, { Refinable } from '../../utils/refine';
import AppLoading from '../AppLoading';

/**
 * 取消授权操作码
 */
export type UnauthorizeCode =
  | /* 授权码移除 */ 'TOKEN_REMOVE'
  | /* 授权码禁用 */ 'TOKEN_FORBIDDEN'
  | /* 授权码缺失 */ 'TOKEN_MISSING'
  | /* 授权码非法 */ 'TOKEN_INVALID'
  | /* 授权码过期 */ 'TOKEN_EXPIRED';

/**
 * 事件取消授权操作码
 */
export type EventsUnauthorizeCode =
  | /* 授权码移除 */ '@EVENT_TOKEN_REMOVE'
  | /* 授权码禁用 */ '@EVENT_TOKEN_FORBIDDEN';

/**
 * 授权事件
 */
export type AuthorizationEvents<T = unknown> = {
  /**
   * 授权事件
   * @param token 授权 token
   * @param payload 授权信息
   */
  authorize: (token: string, payload: T) => void;
  /**
   * 取消授权
   * @param code 操作码
   * @param reason 原因
   */
  unauthorize: (code: EventsUnauthorizeCode | UnauthorizeCode, reason: string) => void;
};

/**
 * 授权事件
 */
export const authorizationEvents = new Emitter<AuthorizationEvents>();

/**
 * 取消授权
 * @param code 操作码
 * @param reason 原因
 */
export function unauthorize(code: EventsUnauthorizeCode, reason: string) {
  authorizationEvents.emit('unauthorize', code, reason);
}

/**
 * 监听授权事件
 * @param handle 授权事件处理函数
 * @returns 取消监听事件
 */
export function onAuthorize<T = unknown>(handle: (token: string, payload: T) => void) {
  return authorizationEvents.on('authorize', (token, payload) => {
    handle(token, payload as T);
  });
}

/**
 * 监听取消授权事件
 * @param handle 取消授权事件处理函数
 * @returns 取消监听事件
 */
export function onUnauthorize(handle: (code: UnauthorizeCode, reason: string) => void) {
  return authorizationEvents.on('unauthorize', (code, reason) => {
    if (code !== '@EVENT_TOKEN_REMOVE' && code !== '@EVENT_TOKEN_FORBIDDEN') {
      handle(code, reason);
    }
  });
}

/**
 * 授权 hook 选项
 */
export interface AuthorizationHookOptions {
  /**
   * 存储对象
   */
  storage?: Storage;
  /**
   * 缓存键名
   */
  cacheKey?: string;
  /**
   * query 键名
   */
  queryKey?: string;
}

/**
 * 授权 hook aoi
 */
export interface AuthorizationHookApi {
  /**
   * 授权中
   */
  loading: boolean;
  /**
   * 是否已经授权
   */
  authorized: boolean;
  /**
   * 授权 token
   */
  token: string | null;
  /**
   * 授权信息
   */
  payload: Itoken | null;
  /**
   * 超极本授权 token
   */
  superBookToken: string | null;
  /**
   * 授权 token 来源
   */
  tokenSource: 'state' | 'query';
  accessToken: string;
  /**
   * 授权
   * @param token 授权 token
   */
  authorize(token: string): void;
  /**
   * 取消授权
   * @param code 操作码
   * @param reason 原因
   */
  unauthorize(code: UnauthorizeCode, reason: string): void;
  /**
   * 创建恢复跳转位置
   * @param fallbackTo 默认路径
   */
  createRestoreToLocation(fallbackTo?: string): PartialLocation;
  /**
   * 创建重定向跳转位置
   * @param to 重定向路径
   */
  createRedirectToLocation(to?: string): PartialLocation;
  /**
   * 创建已授权跳转位置
   */
  createAuthorizedLocation(): PartialLocation;
}
export interface Itoken {
  type: 1 | 2;
  apps: string[];
  seller_ids: string[];
  organization_id: string;
  iss: string;
  iat: number;
  exp: number;
  env: 'stg' | 'prd' | 'pre';
  nick: string;
  legacy: boolean;
  platform: string;
  leyan_user?: {
    nickname: string;
    username: string;
    access_type: string;
  };
  store_id: string;
  store_name: string;
  seller_id: string;
  leyan_seller_id: string;
}
/**
 * 授权 api hook
 * @param options 授权 api hook 选项
 */
export function useAuthorizationApi(options: AuthorizationHookOptions = {}) {
  const { storage = localStorage, cacheKey = '@authorization-token', queryKey = 'token' } = options;

  const location = useLocation();
  const query = useQuery();

  const createSearch = useCreateSearch();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [stateToken, setStateToken] = useState(() => {
    return storage.getItem(cacheKey);
  });
  const [stateSuperBookToken, setStateSuperBookToken] = useState(() => {
    return superBookTokenCache.get();
  });
  const [payload, setPayload] = useState<Itoken | null>(null);
  const { [queryKey]: queryToken, access_token: accessToken } = query;
  const { token, tokenSource } = useMemo(() => {
    if (accessToken) {
      return typeof accessToken === 'string'
        ? {
            token: stateToken,
            tokenSource: 'state',
          }
        : {
            token: queryToken,
            tokenSource: 'query',
          };
    }
    return typeof queryToken === 'string'
      ? {
          token: queryToken,
          tokenSource: 'query',
        }
      : {
          token: stateToken,
          tokenSource: 'state',
        };
  }, [accessToken, queryToken, stateToken]);
  const superBookToken = useMemo(() => {
    return accessToken || stateSuperBookToken;
  }, [accessToken, stateSuperBookToken]);
  const internalApiRef = useTrackingRef({
    isTokenAuthorized(token: string) {
      return authorized && stateToken === token;
    },
    authorize(token: string, payload: Itoken) {
      storage.setItem(cacheKey, token);
      superBookTokenCache.set(superBookToken);
      authorizationEvents.emit('authorize', token, payload);
    },
    unauthorize(code: UnauthorizeCode, reason: string) {
      storage.removeItem(cacheKey);

      authorizationEvents.emit('unauthorize', code, reason);
    },
  });

  const unauthorize = useCallback(
    (code: UnauthorizeCode, reason: string) => {
      internalApiRef.current.unauthorize(code, reason);

      batchedUpdates(() => {
        setStateToken(null);
        setStateSuperBookToken(null);
        setLoading(false);
        setAuthorized(false);
        setPayload(null);
      });
    },
    [internalApiRef],
  );
  const authorize = useCallback(
    async (token: string | null) => {
      setLoading(true);
      if (token === null) {
        unauthorize('TOKEN_MISSING', 'token not provided');

        return;
      }
      if (internalApiRef.current.isTokenAuthorized(token)) {
        setLoading(false);
        return;
      }

      const payload = decode(token, { json: true }) as Itoken;

      if (payload === null) {
        unauthorize('TOKEN_INVALID', `token "${token}" invalid`);

        return;
      }

      if (typeof payload?.exp === 'number' && payload.exp * 1000 < Date.now()) {
        unauthorize('TOKEN_EXPIRED', `token "${token}" expired`);

        return;
      }

      internalApiRef.current.authorize(token, payload);
      Promise.resolve().then(async () => {
        const { seller_id } = payload ?? {};
        batchedUpdates(() => {
          setStateToken(token);
          storeIdCache.set(seller_id);
          setStateSuperBookToken(superBookToken);
          setLoading(false);
          setAuthorized(true);
          setPayload(payload);
        });
      });
    },
    [internalApiRef, superBookToken, unauthorize],
  );
  const createRestoreToLocation = useCallback(
    (fallbackTo) => {
      const { _from: to = fallbackTo, ...otherQuery } = query;

      return {
        pathname: to,
        search: createSearch(otherQuery),
      } as PartialLocation;
    },
    [createSearch, query],
  );
  const createRedirectToLocation = useCallback(
    (to = '/403') => {
      const { pathname } = location;

      return {
        pathname: to,
        search: createSearch({
          ...query,
          _from: pathname,
        }),
      } as PartialLocation;
    },
    [createSearch, location, query],
  );
  const createAuthorizedLocation = useCallback(() => {
    const { [queryKey]: _, access_token: accessToken, ...otherQuery } = query;
    return {
      ...location,
      search: createSearch(otherQuery),
    } as PartialLocation;
  }, [createSearch, location, query, queryKey]);

  useEffect(() => {
    authorize(token);
  }, [accessToken, authorize, location, token]);

  // console.log({
  //   loading,
  //   authorized,
  //   token,
  //   payload,
  //   tokenSource,
  //   authorize,
  //   unauthorize,
  //   createRestoreToLocation,
  //   createRedirectToLocation,
  //   createAuthorizedLocation,
  // })

  return {
    loading,
    authorized,
    token,
    payload,
    tokenSource,
    superBookToken,
    authorize,
    unauthorize,
    createRestoreToLocation,
    createRedirectToLocation,
    createAuthorizedLocation,
    accessToken,
  } as AuthorizationHookApi;
}

/**
 * 授权 api context
 */
export const AuthorizationApiContext = createNamedContext<AuthorizationHookApi | null>(
  'AuthorizationStateContext',
  null,
);

/**
 * 获取授权 api
 */
export function useAuthorization() {
  const api = useContext(AuthorizationApiContext);

  if (api === null) {
    throw new Error(
      'useAuthorization() may be used only in the context of a <Authorization> component.',
    );
  }

  return api as AuthorizationHookApi;
}

const { Provider: AuthorizationApiProvider } = AuthorizationApiContext;

/**
 * 授权容器属性
 */
export interface AuthorizationProps<T = unknown> extends AuthorizationHookOptions {
  /**
   * 授权中替换元素
   */
  fallback?: Refinable<() => React.ReactNode>;
  /**
   * 授权事件处理函数
   * @param token 授权 token
   * @param payload 授权信息
   */
  onAuthorize?(token: string, payload: T): void;
  /**
   * 取消授权事件处理函数
   * @param code 操作码
   * @param reason 原因
   */
  onUnauthorize?(code: UnauthorizeCode, reason: string): void;
  /**
   * 子元素
   */
  children?: Refinable<(api: AuthorizationHookApi) => React.ReactNode>;
}

/**
 * 授权容器
 * @param props 授权容器属性
 */
function Authorization<T = unknown>(props: AuthorizationProps<T>) {
  const {
    fallback = <AppLoading />,
    onAuthorize,
    onUnauthorize,
    storage,
    cacheKey,
    queryKey,
    children,
  } = props;
  const api = useAuthorizationApi({
    storage,
    cacheKey,
    queryKey,
  });
  const parentApiValue = useContext(AuthorizationApiContext);
  const apiValue = useMemo(() => {
    return parentApiValue === null ? api : parentApiValue;
  }, [api, parentApiValue]);

  useLayoutEffect(() => {
    const offAuthorize = authorizationEvents.on('authorize', (token, payload) => {
      if (onAuthorize) {
        onAuthorize(token, payload as T);
      }
    });

    const offUnauthorize = authorizationEvents.on('unauthorize', (code, reason) => {
      if (code === '@EVENT_TOKEN_REMOVE') {
        api.unauthorize('TOKEN_REMOVE', reason);
      } else if (code === '@EVENT_TOKEN_FORBIDDEN') {
        api.unauthorize('TOKEN_FORBIDDEN', reason);
      } else if (onUnauthorize) {
        onUnauthorize(code, reason);
      }
    });

    return () => {
      offAuthorize();
      offUnauthorize();
    };
  }, [api, onAuthorize, onUnauthorize]);
  if (api.loading) {
    return <>{refine(fallback as any)}</>;
  }

  return (
    <AuthorizationApiProvider value={apiValue}>
      {refine(children, apiValue)}
    </AuthorizationApiProvider>
  );
}

export function useStoreId() {
  const authorizationApiContext = useContext(AuthorizationApiContext);
  return authorizationApiContext?.payload?.store_id;
}

export function useLeyanSellerId() {
  const authorizationApiContext = useContext(AuthorizationApiContext);
  return authorizationApiContext?.payload?.leyan_seller_id;
}
export default Authorization;
